import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from './Header';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CheckCircle2, Award, Users, Clock, Shield, Loader2, Building, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from './ui/sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '../lib/utils';

const DemoRequestPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    employees: '',
    locations: '',
    currentSolution: '',
    specificNeeds: '',
    preferredDate: null,
    preferredTime: '',
    demoType: 'online',
    acceptTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ valid: true, message: '' });
  const [completionProgress, setCompletionProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
  }, []);

  const MAX_CHARS = 500;

  const industries = [
    'Spielhalle',
    'Wettbüro',
    'Gastronomie mit Geldspielgeräten',
    'Veranstaltungen',
    'Casino',
    'Sonstige'
  ];

  const timeSlots = [
    '09:00 - 10:00',
    '10:00 - 11:00',
    '11:00 - 12:00',
    '13:00 - 14:00',
    '14:00 - 15:00',
    '15:00 - 16:00',
    '16:00 - 17:00'
  ];

  // Calculate form completion progress
  useEffect(() => {
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'company', 'industry'];
    const filledFields = requiredFields.filter(field => formData[field]).length;
    setCompletionProgress((filledFields / requiredFields.length) * 100);
  }, [formData]);

  const validateEmail = (email) => {
    if (!email) {
      setEmailValidation({ valid: true, message: '' });
      return;
    }

    const freeEmailProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'gmx.de', 'web.de', 'outlook.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();

    if (freeEmailProviders.includes(domain)) {
      setEmailValidation({
        valid: false,
        message: 'Bitte verwenden Sie Ihre geschäftliche E-Mail-Adresse'
      });
    } else if (domain) {
      setEmailValidation({
        valid: true,
        message: '✓ Geschäftliche E-Mail erkannt'
      });
    }
  };

  // Smart phone number formatting
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.startsWith('49')) {
      const match = cleaned.match(/^(49)(\d{0,3})(\d{0,8})$/);
      if (match) {
        return `+${match[1]}${match[2] ? ' ' + match[2] : ''}${match[3] ? ' ' + match[3] : ''}`;
      }
    } else if (cleaned.startsWith('0')) {
      const match = cleaned.match(/^(\d{0,4})(\d{0,8})$/);
      if (match) {
        return `${match[1]}${match[2] ? ' ' + match[2] : ''}`;
      }
    }

    return value;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if form is valid (HTML5 validation)
    const form = e.target;
    if (!form.checkValidity()) {
      // Find first invalid field and scroll to it
      const firstInvalidField = form.querySelector(':invalid');
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalidField.focus();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';

      // Convert preferredDate to ISO string for backend
      const formDataToSubmit = {
        ...formData,
        preferredDate: formData.preferredDate ? formData.preferredDate.toISOString().split('T')[0] : ''
      };

      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'demo_request',
          form_data: formDataToSubmit,
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Demo-Anfrage erfolgreich gesendet!', {
          description: 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
          duration: 5000,
          icon: <CheckCircle2 className="w-5 h-5" />
        });

        // Reset form
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', company: '',
          industry: '', employees: '', locations: '', currentSolution: '',
          specificNeeds: '', preferredDate: null, preferredTime: '', demoType: 'online',
          acceptTerms: false
        });
        setEmailValidation({ valid: true, message: '' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (result.blocked) {
        toast.error('Online-Anfrage nicht verfügbar', {
          description: result.contact_message || 'Bitte kontaktieren Sie uns telefonisch oder per E-Mail.',
          duration: 8000
        });
      } else {
        toast.error('Fehler beim Senden', {
          description: result.message || 'Bitte versuchen Sie es erneut.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Verbindungsfehler', {
        description: 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        duration: 4000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Live Demo anfragen - IDA KI</title>
        <meta
          name="description"
          content="Erleben Sie die Zukunft der KI-gestützten Compliance. Vereinbaren Sie eine persönliche Demo und entdecken Sie, wie IDA KI Ihre Prozesse revolutioniert."
        />
        <meta name="keywords" content="IDA Demo, Compliance Demo, KI Demonstration, Glücksspiel Compliance Software" />
        <link rel="canonical" href="https://idaki.de/demo-anfrage" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header variant="legal" />

        <main className="pt-32 pb-24 md:pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:justify-between items-start gap-12 lg:gap-20">

              {/* Left Column - Hero Content */}
              <div className="space-y-12 w-full lg:w-auto lg:flex-1 lg:max-w-[550px]">
                <div className="space-y-3">
                  <div className="inline-block">
                    <span className="font-semibold text-teal-700 uppercase tracking-wide">
                      Persönliche Live-Demo
                    </span>
                  </div>

                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                    Erleben Sie die Zukunft der KI-gestützten Compliance
                  </h1>

                  <p className="text-gray-700 leading-relaxed pt-3">
                    Vereinbaren Sie eine individuelle Demo und entdecken Sie, wie IDA KI
                    Ihre Compliance-Prozesse revolutioniert. Unser Experten-Team zeigt Ihnen
                    live, wie Sie Zeit sparen und gleichzeitig rechtssicher bleiben.
                  </p>
                </div>

                {/* Feature List */}
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Maßgeschneiderte Demonstration
                      </h3>
                      <p className="text-gray-700">
                        Umfassende Vorstellung der IDA-Funktionen, speziell angepasst
                        an Ihre Branche und Anforderungen.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Interaktive Experten-Session
                      </h3>
                      <p className="text-gray-700">
                        Stellen Sie Fragen direkt an unsere Compliance-Experten und
                        erhalten Sie sofortige Antworten auf Ihre spezifischen Herausforderungen.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Individueller Implementierungsplan
                      </h3>
                      <p className="text-gray-700">
                        Erfahren Sie, wie IDA nahtlos in Ihre bestehenden Prozesse
                        integriert werden kann und welche Vorteile Sie erwarten.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Keine Verpflichtungen
                      </h3>
                      <p className="text-gray-700">
                        Kostenlose und unverbindliche Demo - überzeugen Sie sich selbst
                        von den Möglichkeiten der KI-gestützten Compliance.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Proof */}
                <div className="mt-8 bg-gradient-to-br from-teal-50 via-blue-50 to-white rounded-2xl shadow-xl p-8 border-2 border-teal-700 relative overflow-hidden">
                  {/* Decorative Quote Mark */}
                  <div className="absolute top-4 right-4 opacity-10">
                    <svg className="w-24 h-24 text-teal-700" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
                    </svg>
                  </div>

                  <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Diese Woche bereits 23 Demos vereinbart
                        </p>
                      </div>
                    </div>

                    <div className="text-gray-700 leading-relaxed border-l-4 border-teal-700 pl-4">
                      <p className="italic">
                        Die Demo hat uns sofort überzeugt. Innerhalb von 2 Wochen waren wir live.
                      </p>
                      <p className="mt-3">
                        – Michael K., Spielhallen-Betreiber
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="pt-8 border-t border-gray-200">
                  <p className="font-semibold text-gray-500 uppercase tracking-wide mb-6">
                    Vertraut von führenden Unternehmen
                  </p>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
                      <Award className="w-8 h-8 text-teal-600 mb-2" />
                      <div className="text-center">
                        <p className="text-xs font-semibold text-gray-900">DSGVO</p>
                        <p className="text-xs text-gray-700">Konform</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
                      <Users className="w-8 h-8 text-teal-600 mb-2" />
                      <div className="text-center">
                        <p className="text-xs font-semibold text-gray-900">500+</p>
                        <p className="text-xs text-gray-700">Standorte</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
                      <Shield className="w-8 h-8 text-teal-600 mb-2" />
                      <div className="text-center">
                        <p className="text-xs font-semibold text-gray-900">ISO 27001</p>
                        <p className="text-xs text-gray-700">Zertifiziert</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Form Card */}
              <div className="w-full lg:w-auto lg:flex-shrink-0 lg:max-w-[465px]">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
                  {/* Sticky Header with Progress */}
                  <div className="lg:sticky lg:top-14 z-10 bg-teal-100 border-b border-teal-200 p-6 pb-4 rounded-t-2xl shadow-sm">
                    <div className="mb-4">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Demo-Termin vereinbaren
                      </h2>
                      <p className="text-gray-700">
                        Wir melden uns innerhalb von 24 Stunden.
                      </p>
                    </div>

                    {/* Progress Indicator */}
                    {completionProgress > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">
                            Fortschritt: {Math.round(completionProgress)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-teal-600 h-1.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${completionProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Form Content */}
                  <form onSubmit={handleSubmit} className="p-8 pt-6 space-y-6" aria-label="Demo-Anfrage Formular">
                    {/* Section 1: Kontaktdaten */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2">
                        <div className="h-px bg-gray-200 flex-1" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Ihre Kontaktdaten
                        </span>
                        <div className="h-px bg-gray-200 flex-1" />
                      </div>

                      {/* Name Fields */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label htmlFor="firstName" className="font-medium text-gray-700">
                              Vorname
                            </Label>
                            <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                              Pflichtfeld
                            </span>
                          </div>
                          <Input
                            id="firstName"
                            name="firstName"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            aria-required="true"
                            className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                            placeholder="Max"
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label htmlFor="lastName" className="font-medium text-gray-700">
                              Nachname
                            </Label>
                            <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                              Pflichtfeld
                            </span>
                          </div>
                          <Input
                            id="lastName"
                            name="lastName"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            aria-required="true"
                            className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                            placeholder="Mustermann"
                          />
                        </div>
                      </div>

                      {/* Email with validation */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="email" className="font-medium text-gray-700">
                            Geschäftliche E-Mail
                          </Label>
                          <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                            Pflichtfeld
                          </span>
                        </div>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          value={formData.email}
                          onChange={(e) => {
                            handleInputChange(e);
                            validateEmail(e.target.value);
                          }}
                          required
                          aria-required="true"
                          aria-describedby={formData.email && emailValidation.message ? "email-helper" : undefined}
                          aria-invalid={!emailValidation.valid && formData.email ? "true" : "false"}
                          className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                          placeholder="beispiel@unternehmen.de"
                        />
                        {formData.email && emailValidation.message && (
                          <p
                            id="email-helper"
                            role="status"
                            className={`text-xs mt-1.5 flex items-center gap-1 ${emailValidation.valid ? 'text-green-600' : 'text-amber-600'}`}
                          >
                            {emailValidation.message}
                          </p>
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="phone" className="font-medium text-gray-700">
                            Telefonnummer
                          </Label>
                          <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                            Pflichtfeld
                          </span>
                        </div>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          required
                          aria-required="true"
                          className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                          placeholder="+49 123 456789"
                        />
                      </div>
                    </div>

                    {/* Section 2: Unternehmensdaten */}
                    <div className="space-y-4 pt-6">
                      <div className="flex items-center gap-2 pb-2">
                        <div className="h-px bg-gray-200 flex-1" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Unternehmensinformationen
                        </span>
                        <div className="h-px bg-gray-200 flex-1" />
                      </div>

                      {/* Company Name */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="company" className="font-medium text-gray-700">
                            Unternehmensname
                          </Label>
                          <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                            Pflichtfeld
                          </span>
                        </div>
                        <Input
                          id="company"
                          name="company"
                          autoComplete="organization"
                          value={formData.company}
                          onChange={handleInputChange}
                          required
                          aria-required="true"
                          className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                          placeholder="Ihr Unternehmen"
                        />
                      </div>

                      {/* Industry */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="industry" className="font-medium text-gray-700">
                            Branche
                          </Label>
                          <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                            Pflichtfeld
                          </span>
                        </div>
                        <Select
                          value={formData.industry}
                          onValueChange={(value) => handleSelectChange('industry', value)}
                        >
                          <SelectTrigger className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200">
                            <SelectValue placeholder="Wählen Sie Ihre Branche" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((industry) => (
                              <SelectItem key={industry} value={industry}>
                                {industry}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Employees and Locations */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label htmlFor="employees" className="font-medium text-gray-700">
                              Mitarbeiter
                            </Label>
                            <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
                              Optional
                            </span>
                          </div>
                          <Select
                            value={formData.employees}
                            onValueChange={(value) => handleSelectChange('employees', value)}
                          >
                            <SelectTrigger className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200">
                              <SelectValue placeholder="Anzahl" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1-5">1-5</SelectItem>
                              <SelectItem value="6-20">6-20</SelectItem>
                              <SelectItem value="21-50">21-50</SelectItem>
                              <SelectItem value="51-100">51-100</SelectItem>
                              <SelectItem value="100+">100+</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label htmlFor="locations" className="font-medium text-gray-700">
                              Standorte
                            </Label>
                            <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
                              Optional
                            </span>
                          </div>
                          <Input
                            id="locations"
                            name="locations"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={formData.locations}
                            onChange={handleInputChange}
                            className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                            placeholder="z.B. 3"
                          />
                        </div>
                      </div>

                      {/* Conditional Current Solution Field */}
                      {(formData.employees && formData.employees !== '1-5') && (
                        <div className="space-y-4 p-4 bg-teal-50 rounded-lg border border-teal-100">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Building className="w-4 h-4 text-teal-700" />
                              <Label htmlFor="currentSolution" className="font-medium text-gray-900">
                                Nutzen Sie bereits eine Compliance-Lösung?
                              </Label>
                            </div>
                            <Select
                              value={formData.currentSolution}
                              onValueChange={(value) => handleSelectChange('currentSolution', value)}
                            >
                              <SelectTrigger className="mt-1.5 bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200">
                                <SelectValue placeholder="Bitte wählen..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="keine">Keine Lösung</SelectItem>
                                <SelectItem value="excel">Excel/Manuelle Prozesse</SelectItem>
                                <SelectItem value="software">Andere Software</SelectItem>
                                <SelectItem value="teilweise">Teilweise automatisiert</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-700 mt-1.5">
                              Hilft uns, die Demo besser auf Ihre Situation abzustimmen
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Section 3: Demo Preferences */}
                    <div className="pt-6 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-teal-600" />
                        Demo-Präferenzen (Optional)
                      </h3>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="preferredDate" className="font-medium text-gray-700">
                              Wunschtermin
                            </Label>
                            {isMobile ? (
                              <Input
                                id="preferredDate"
                                name="preferredDate"
                                type="date"
                                value={formData.preferredDate ? format(formData.preferredDate, 'yyyy-MM-dd') : ''}
                                onChange={(e) => {
                                  const date = e.target.value ? new Date(e.target.value) : null;
                                  setFormData(prev => ({ ...prev, preferredDate: date }));
                                }}
                                className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                                min={new Date().toISOString().split('T')[0]}
                              />
                            ) : (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full justify-start text-left font-normal mt-1.5 h-10",
                                      "focus:ring-2 focus:ring-teal-500 focus:border-teal-500",
                                      "transition-all duration-200 hover:border-teal-400",
                                      !formData.preferredDate && "text-muted-foreground"
                                    )}
                                  >
                                    <CalendarIcon className="mr-2 h-4 w-4 text-teal-600" />
                                    {formData.preferredDate ? format(formData.preferredDate, "dd.MM.yyyy", { locale: de }) : "tt.mm.jjjj"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={formData.preferredDate}
                                    onSelect={(date) => setFormData(prev => ({ ...prev, preferredDate: date }))}
                                    disabled={(date) => date < new Date().setHours(0, 0, 0, 0)}
                                    fromDate={new Date()}
                                    fromMonth={new Date()}
                                    locale={de}
                                    initialFocus
                                    formatters={{
                                      formatWeekdayName: (date) => format(date, 'EEEEEE', { locale: de })
                                    }}
                                    classNames={{
                                      selected: "bg-teal-600 text-white hover:bg-teal-700 focus:bg-teal-700",
                                      today: "border border-teal-500 text-teal-600 font-semibold",
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="preferredTime" className="font-medium text-gray-700">
                              Wunschzeit
                            </Label>
                            <Select
                              value={formData.preferredTime}
                              onValueChange={(value) => handleSelectChange('preferredTime', value)}
                            >
                              <SelectTrigger className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200">
                                <SelectValue placeholder="Zeit wählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {timeSlots.map((time) => (
                                  <SelectItem key={time} value={time}>
                                    {time}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information with character counter */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="specificNeeds" className="font-medium text-gray-700">
                          Besondere Anforderungen
                        </Label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
                            Optional
                          </span>
                          <span className="text-xs text-gray-500">
                            {formData.specificNeeds.length}/{MAX_CHARS}
                          </span>
                        </div>
                      </div>
                      <Textarea
                        id="specificNeeds"
                        name="specificNeeds"
                        value={formData.specificNeeds}
                        onChange={handleInputChange}
                        maxLength={MAX_CHARS}
                        className="mt-1.5 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
                        placeholder="Gibt es spezielle Funktionen, die Sie besonders interessieren?"
                        rows={3}
                      />
                      {formData.specificNeeds.length > 400 && (
                        <p className="text-xs text-amber-600 mt-1.5">
                          Versuchen Sie bitte, die wichtigsten Punkte zusammenzufassen
                        </p>
                      )}
                    </div>

                    {/* Terms Checkbox */}
                    <div className="pt-4">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="acceptTermsDemo"
                          checked={formData.acceptTerms}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptTerms: checked }))}
                          className="mt-1 w-5 h-5 md:w-4 md:h-4"
                        />
                        <Label htmlFor="acceptTermsDemo" className="text-gray-700 leading-relaxed">
                          Ich akzeptiere die{' '}
                          <a href="/agb" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium underline">
                            AGB
                          </a>
                          {' '}und stimme der Kontaktaufnahme sowie Verarbeitung meiner Daten zum Zweck
                          der Demo-Organisation zu. Weitere Informationen finden Sie in unserer{' '}
                          <a href="/datenschutz" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium underline">
                            Datenschutzerklärung
                          </a>.
                        </Label>
                      </div>
                    </div>

                    {/* Value Proposition Restatement */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700">
                            Kostenlose Demo • Keine Verpflichtung • Antwort in 24h
                          </p>
                        </div>
                      </div>

                      {/* Submit Button - Desktop */}
                      <div className="hidden md:block">
                        <Button
                          type="submit"
                          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Wird gesendet...
                            </span>
                          ) : (
                            'Demo anfragen'
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

            </div>
          </div>
        </main>

        {/* Sticky Mobile CTA */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-40">
          <Button
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('form').dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Wird gesendet...
              </span>
            ) : (
              'Demo anfragen'
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default DemoRequestPage;
